import { TpSectionsIndexContext } from '../../../components/sections-index/SectionsIndex';
import type { TpSectionElementDTO } from '../../../data/dashboard-template/schemas';
import { TpDataContext } from '../../../data/interfaces';
import { TpUserEnSelectionAction } from '../../../data/selection/userSelectionActions';
import { TpChartConfigParams, TpChartDataBinding } from '../charts/chartDataBindings';
import { TpElementLayoutHints } from './layout-hints';
import { TpTemplateInfo } from '../../../api/models/assets';

export enum DASHBOARDELEMIDS {
  Dashboard = 'Dashboard',
  Section = 'Section',
  HorizontalLayout = 'HorizontalLayout',
  VerticalLayout = 'VerticalLayout',
  SequenceLayout = 'SequenceLayout',
  Tabber = 'Tabber',
  Widget = 'Widget',
  StaticText = 'StaticText',
  Bitmap = 'Bitmap',
  ActionButtons = 'ActionButtons',
  TopBarTables = 'TopBarTables',
}

/**
 * A note on the "id" property of most of these TpDashboardElement instances:
 * they are generated with get_guid() for widgets, layout nodes,
 * and generated dashboard elements are ephemeral runtime identity. They are
 * used for React keys and Redux tree lookup through findDashboardElement()
 * during the current session, then regenerated when the dashboard is rebuilt.
 * They must not become a cross-session serialization contract; persisted
 * dashboard identity comes from semantic fields such as section IDs and
 * widget selectors instead.
 */
// Union type for any type of element that can be on a dashboard (inckuding the dashboard itself)
export type TpDashboardElement =
  | TpDashboardDefinition
  | TpDashboardHorizontalLayoutDefinition
  | TpDashboardVerticalLayoutDefinition
  | TpDashboardSequenceLayoutDefinition
  | TpChartDefinition
  | TpStaticTextDefinition
  | TpDashboardSectionDefinition
  | TpDashboardTabberDefinition
  | TpBitmapDefinition
  | TpActionButtonsDefinition;

// Definition of a configured & realised chart in a dashboard, with aspect bindings & config specified
export interface TpChartDefinition {
  elemType: DASHBOARDELEMIDS.Widget;
  id: string;
  selector?: string; // optional selector to identify the chart in the template (e.g. in a gallery plot).
  elemLayoutHints?: TpElementLayoutHints;
  chartType: string;
  dataBinding: TpChartDataBinding;
  config: TpChartConfigParams;
}

export function isChartDefinition(elem: TpDashboardElement): elem is TpChartDefinition {
  return elem.elemType === DASHBOARDELEMIDS.Widget;
}

// Returns true when two dashboard contexts are identical
export function compareChartDefinitions(prev: TpChartDefinition, next: TpChartDefinition) {
  return JSON.stringify(prev) == JSON.stringify(next);
}

export type TpDashboardElementWithLayoutHints =
  | TpChartDefinition
  | TpStaticTextDefinition
  | TpBitmapDefinition
  | TpDashboardSectionDefinition
  | TpDashboardTabberDefinition
  | TpDashboardHorizontalLayoutDefinition
  | TpDashboardVerticalLayoutDefinition
  | TpDashboardSequenceLayoutDefinition;

export function supportsElemLayoutHints(elem: TpDashboardElement): elem is TpDashboardElementWithLayoutHints {
  return [
    DASHBOARDELEMIDS.Widget,
    // FIXME: Static texts currently ignore layout hints: cannot allow now unless we migrate existing templates
    // DASHBOARDELEMIDS.StaticText,
    DASHBOARDELEMIDS.Bitmap,
    DASHBOARDELEMIDS.Section,
    DASHBOARDELEMIDS.Tabber,
    DASHBOARDELEMIDS.HorizontalLayout,
    DASHBOARDELEMIDS.VerticalLayout,
    DASHBOARDELEMIDS.SequenceLayout,
  ].includes(elem.elemType);
}

export interface TpStaticTextDefinition {
  elemType: DASHBOARDELEMIDS.StaticText;
  id: string;
  elemLayoutHints?: TpElementLayoutHints;
  content: string;
}

export interface TpBitmapDefinition {
  elemType: DASHBOARDELEMIDS.Bitmap;
  id: string;
  elemLayoutHints: TpElementLayoutHints;
  description: string;
}

export interface TpActionButtonsDefinition {
  elemType: DASHBOARDELEMIDS.ActionButtons;
  actionDefs: any[];
}

export interface TpCustomCategoricalOrderConfig {
  canonicalTypeId: string;
  propertyId: string;
  order: string[];
}

export interface TpGlobalChartColorMappingConfig {
  customCategoricalOrders?: TpCustomCategoricalOrderConfig[];
}

export interface TpDashboardGlobalChartConfig {
  colorMapping?: TpGlobalChartColorMappingConfig;
}

export interface TpDashboardDefinition {
  elemType: DASHBOARDELEMIDS.Dashboard;
  id: string; // Asset ID
  name: string;
  elemLayoutHints?: TpElementLayoutHints;
  elements: TpDashboardSectionDefinition[];
  dashboardActionDefs: { [actionId: string]: TpDashboardActionDefinition };
  topbarTables?: TpDashboardTopbarDefinition;
  widgetGlobalConfig: TpDashboardGlobalChartConfig;
  templateInfo: TpTemplateInfo;
}

export interface TpDashboardSectionDefinition {
  elemType: DASHBOARDELEMIDS.Section;
  id: string;
  elemLayoutHints?: TpElementLayoutHints;
  title: string;
  description?: string;
  requiredTableId?: string;
  collapsed?: boolean;
  elements: TpDashboardElement[];
}

export interface TpDashboardTabberDefinition {
  elemType: DASHBOARDELEMIDS.Tabber;
  id: string;
  elemLayoutHints?: TpElementLayoutHints;
  tabTitles: string[];
  elements: TpDashboardElement[];
}

export interface TpDashboardHorizontalLayoutDefinition {
  id: string;
  elemType: DASHBOARDELEMIDS.HorizontalLayout;
  elemLayoutHints?: TpElementLayoutHints;
  elements: TpDashboardElement[];
}

export interface TpDashboardVerticalLayoutDefinition {
  id: string;
  elemType: DASHBOARDELEMIDS.VerticalLayout;
  elemLayoutHints: TpElementLayoutHints;
  elements: TpDashboardElement[];
}

export interface TpDashboardSequenceLayoutDefinition {
  id: string;
  elemType: DASHBOARDELEMIDS.SequenceLayout;
  elemLayoutHints?: TpElementLayoutHints;
  elements: TpDashboardElement[];
}

export function isSequenceLayoutElement(
  elem: TpDashboardElement | undefined,
): elem is TpDashboardSequenceLayoutDefinition {
  return elem?.elemType === DASHBOARDELEMIDS.SequenceLayout;
}

export interface TpDashboardActionDefinition {
  id: string;
}

export interface TpDashboardTopbarDefinition {
  elemType: DASHBOARDELEMIDS.TopBarTables;
  tableSelection: string[];
}

// Contains all context information needed to render a (part of a) dashboard
export interface TpDashboardContext {
  dashboardDefId: string;
  dashboardInstanceId: string; // unique identifier of a specific dashboard instance
  editMode: boolean;
  dashboardActionDefs: { [actionId: string]: TpDashboardActionDefinition };
  widgetGlobalConfig?: TpDashboardGlobalChartConfig;
  handleOpenItem: (canonicalTypeId: string, itemId: string) => void;
  addChart: (chartDef: TpChartDefinition, dashboardDefId: string, targetSectionId?: string) => void;
  updateSingleChartFilter: (chartId: string, newFilter: any) => void;
  updateChartCustomTitle: (chartId: string, newTitle: string) => void;
  updateChartCustomDescription: (chartId: string, newTitle: string) => void;
  updateChartConfigParam: (chartId: string, configParamId: string, value: any) => void;
  updateChartAspectBinding: (chartId: string, aspectId: string, newColId: string) => void;
  updateChartCompoundAspectBinding: (
    chartId: string,
    compoundAspectId: string,
    newColId: string,
    method: string,
  ) => void;
  addElementToSection: (
    element: TpSectionElementDTO,
    dashboardDefId: string,
    targetSectionId?: string,
    ctx?: TpDashboardCreationContext,
  ) => void;
  getChartDefsFromTemplateId(chartId: string): TpChartDefinition[];
  getAsyncTableColumn: (dataTableId: string, columnId: string) => Promise<string[]>;
  elementVerticalSizeOffset: number; // when used for a (nested) element, indicates what offset should be subtracted from the vertical size to achieve a good alignement (e.g. because of a parent tabber)
  dataContextStatusIndex: number; // included here to ensure that a component update is triggered when the data context changes (e.g. a modification of the selection)
  sectionIndexContext: TpSectionsIndexContext;
}

// Contains all the context needed to instantiate an actual dashboard from a template
export interface TpDashboardCreationContext {
  dataContext: TpDataContext;
  contextVariables: { [variableName: string]: string }; // context variable can be set by higher order components (e.g. a gallery looping over a set of properties), and used by individual elements
}

export interface TpDashboardUpdateContext {
  dataContextId: string;
  contextVariables: { [variableName: string]: string }; // context variable can be set by higher order components (e.g. a gallery looping over a set of properties), and used by individual elements
}

// Returns true when two dashboard contexts are identical
export function compareDashboardContexts(prev: TpDashboardContext, next: TpDashboardContext) {
  return JSON.stringify(prev) == JSON.stringify(next);
}

// Contains all context information needed to render a specific widget
export interface TpWidgetContext {
  dashboardContext: TpDashboardContext;
  widgetId: string;
  zoomingActive: boolean;
  handleUserSelectionAction: (action: TpUserEnSelectionAction) => void;
  toggleSingleItemSelection: (itemId: string) => void;
  setBitmapDownloadFunction?: (downloadFunction: any) => void;
  handleOpenItem: (canonicalTypeId: string, itemId: string) => void;
}

export interface TpAssetViewElement {
  type: string;
  title: string;
  description?: string;
  properties?: any[];
  related_asset_type?: string;
  relation_type?: string;
  content?: string;
}

export interface TpAssetView {
  assetview_elements: TpAssetViewElement[];
}
